import React, { useState } from 'react';
import {
  Box,
  Toolbar,
  Tooltip,
  IconButton,
  Typography,
  Grid,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Autocomplete,
  Collapse,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import CloseIcon from '@mui/icons-material/Close';

import Loader from '../Global/Loader';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getBankBranchData, setBranchDetails } from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { DownloadReceivableRecords } from '../../store/Slice/Receive/ReceiveSlice';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';

export default function ReceivableFileToolbar({
  selected,
  setFilterParameter,
  filterParameter,
  isOpenFilter,
  setIsOpenFilter
}) {
  const [openLoader, setOpenLoader] = useState(false);
  const [downloadPopup, setDownloadPopup] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { LoanTypeDrop, BankBranchDrop, BankDrop, stateDrop } = useGlobalSelector();

  const handleRemoveFilter = () => {
    setFilterParameter({
      loanTypeId: null,
      bankId: null,
      branchId: null,
      provinceId: null
    });
  };

  const handleDownloadReceivableReports = async () => {
    try {
      setDownloadPopup(false);
      setOpenLoader(true);
      const credentials = {
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        accessLevelId: LoginData?.accessLevelId,
        entityListRequestModel: {
          searchLiteral: null,
          pageSize: 1000,
          sortOrder: 'asc',
          pageNumber: 1
        },
        filterModel: {
          bankId: filterParameter?.bankId || null,
          branchId: filterParameter?.branchId || null,
          loanTypeId: filterParameter?.loanTypeId || null,
          provinceId: filterParameter?.provinceId || null,
          receivableIds: selected?.length > 0 ? selected.map((item) => item?.receivableId) : []
        },
        revenueFileStatusId: 128
      };

      const DownloadReceivableReportsResponse = await dispatch(
        DownloadReceivableRecords(credentials)
      );

      let blob;
      if (
        DownloadReceivableReportsResponse &&
        typeof DownloadReceivableReportsResponse.blob === 'function'
      ) {
        blob = await DownloadReceivableReportsResponse.blob();
      } else if (
        DownloadReceivableReportsResponse &&
        typeof DownloadReceivableReportsResponse === 'object' &&
        DownloadReceivableReportsResponse.data
      ) {
        const csv = DownloadReceivableReportsResponse.data;
        blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      } else if (typeof DownloadReceivableReportsResponse === 'string') {
        blob = new Blob([DownloadReceivableReportsResponse], { type: 'text/csv;charset=utf-8;' });
      } else {
        throw new Error('Unexpected download response format');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'recievable-mis-report.csv');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      console.error('error', error);
    } finally {
      setOpenLoader(false);
    }
  };

  const handleButtonClick = () => {
    if (selected && selected.length > 0) {
      const bankIds = selected.map((item) => item.bankId);
      const branchNames = selected.map((item) => item.branchName);
      const allSameBankName = bankIds.every((bankId) => bankId === bankIds[0]);
      const allSameBranchName = branchNames.every((branchName) => branchName === branchNames[0]);

      if (allSameBankName && allSameBranchName) {
        localStorage.setItem('selectedInvoices', JSON.stringify(selected));
        navigate('view-multiple-invoice', {
          state: {
            selectedItem: selected
          }
        });
      } else {
        dispatch(
          setApiError(
            'Selected items have different bank or branch names. Please select same bank and branch names files.'
          )
        );
      }
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      <Loader openLoader={openLoader} />

      {/* Action Buttons Row */}
      <Toolbar
        sx={{
          minHeight: '60px !important',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        {/* + Button */}
        <Tooltip title="Generate / Raise Invoice" placement="top" arrow>
          <span>
            <IconButton
              disabled={!selected || selected.length === 0}
              onClick={handleButtonClick}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '8px',
                border: '1.5px solid #bae6fd',
                bgcolor: '#f0f9ff',
                color: '#0284c7',
                '&:hover': {
                  bgcolor: '#e0f2fe',
                  borderColor: '#7dd3fc'
                },
                '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>

        {/* Cloud Download Button */}
        <Tooltip title="Download Receivable Report" placement="top" arrow>
          <IconButton
            onClick={() => setDownloadPopup(true)}
            sx={{
              width: 38,
              height: 38,
              borderRadius: '8px',
              border: '1.5px solid #bae6fd',
              bgcolor: '#f0f9ff',
              color: '#0284c7',
              '&:hover': {
                bgcolor: '#e0f2fe',
                borderColor: '#7dd3fc'
              }
            }}
          >
            <CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* Collapsible Filters */}
      <Collapse in={Boolean(isOpenFilter)}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            borderBottom: '1px solid #e2e8f0',
            mx: 2,
            mb: 2,
            borderRadius: '12px'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.8}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={filterParameter?.provinceId ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const cleaned = raw === '' ? null : raw;
                  setFilterParameter((pre) => ({
                    ...pre,
                    provinceId: cleaned
                  }));
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  fontSize: 13,
                  '& fieldset': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="">All States</MenuItem>
                {stateDrop?.map((item) => (
                  <MenuItem key={item?.value} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={2.8}>
              <Autocomplete
                disablePortal
                id="combo-box-bank"
                options={BankDrop || []}
                size="small"
                value={BankDrop?.find((opt) => opt.value === filterParameter?.bankId) || null}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                onChange={(e, newValue) => {
                  setFilterParameter((pre) => ({
                    ...pre,
                    branchId: null,
                    bankId: newValue?.value || null
                  }));
                  dispatch(setBranchDetails([]));
                  if (newValue?.value) {
                    dispatch(getBankBranchData(newValue.value));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Bank"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.8}>
              <Autocomplete
                disablePortal
                disabled={!filterParameter?.bankId}
                id="combo-box-branch"
                options={BankBranchDrop || []}
                size="small"
                value={
                  BankBranchDrop?.find((opt) => opt.value === filterParameter?.branchId) || null
                }
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                onChange={(e, newValue) => {
                  setFilterParameter((pre) => ({
                    ...pre,
                    branchId: newValue?.value || null
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Branch"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.8}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={filterParameter?.loanTypeId ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const cleaned = raw === '' ? null : raw;
                  setFilterParameter((pre) => ({
                    ...pre,
                    loanTypeId: cleaned
                  }));
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  fontSize: 13,
                  '& fieldset': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="">All Products</MenuItem>
                {LoanTypeDrop?.map((item) => (
                  <MenuItem key={item?.value} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm="auto">
              <Tooltip title="Reset Filters">
                <IconButton
                  onClick={handleRemoveFilter}
                  sx={{
                    color: '#dc2626',
                    bgcolor: '#fee2e2',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Download confirmation dialog */}
      <Dialog
        open={downloadPopup}
        onClose={() => setDownloadPopup(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: 3,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
            Download Reports
          </Typography>
          <IconButton
            size="small"
            onClick={() => setDownloadPopup(false)}
            sx={{ color: '#64748b' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, color: '#475569' }}>
            Are you sure you want to download the receivable reports?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="outlined"
            onClick={() => setDownloadPopup(false)}
            sx={{ borderRadius: '8px', color: '#64748b', borderColor: '#cbd5e1' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadReceivableReports}
            sx={{
              borderRadius: '8px',
              bgcolor: '#0284c7',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
